/**
 * API client: thin wrapper around fetch for the Invyo backend.
 * All requests include credentials (cookies) for cookie-based auth.
 * Automatically refreshes expired access tokens via POST /auth/refresh.
 * Sends CSRF token from cookie on every mutation (POST/PATCH/DELETE); the server
 * decides where the token is required, the client does not second-guess it. On
 * pre-session calls (login, reset-password, …) there is no cookie yet, so no header
 * is attached and nothing changes.
 * Fires onSessionExpired when both access + refresh tokens are invalid.
 */

import { httpErrorMessage } from './http-error-message';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4010';

interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  error?: string;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

class ApiError extends Error {
  status: number;
  /** Seconds the client should wait before retrying (set on login throttling) */
  retryAfter: number | null;
  /** The input a 409 or a 400 is about, when the server names one (#755); a form shows the message under it. */
  field: string | null;
  constructor(message: string, status: number, retryAfter: number | null = null, field: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfter = retryAfter;
    this.field = field;
  }
}

// ── Session expiry callback ──
// AuthContext registers this so the client can trigger auto-logout.
let sessionExpiredCallback: (() => void) | null = null;

export function onSessionExpired(cb: () => void) {
  sessionExpiredCallback = cb;
}

// Track refresh in-flight to avoid parallel refresh calls
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Read CSRF token from the csrf_token cookie (supports prefixed cookie names)
 */
function getCsrfToken(): string | null {
  // Find a cookie whose name ends with csrf_token (supports prefixed cookies)
  const cookies = document.cookie.split('; ');
  for (const cookie of cookies) {
    const eqIndex = cookie.indexOf('=');
    if (eqIndex === -1) continue;
    const name = cookie.substring(0, eqIndex);
    if (name.endsWith('csrf_token')) {
      return decodeURIComponent(cookie.substring(eqIndex + 1));
    }
  }
  return null;
}

async function requestRaw<T>(
  path: string,
  options: RequestInit = {},
  _isRetry = false,
): Promise<ApiResponse<T>> {
  const fullPath = path.startsWith('/api/') ? path : `/v1${path}`;
  const url = `${API_BASE_URL}${fullPath}`;

  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(options.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  const method = (options.method ?? 'GET').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['x-csrf-token'] = csrfToken;
  }

  const res = await fetch(url, { ...options, credentials: 'include', headers });

  if (res.status === 204) return { success: true, data: undefined as T };

  // Only tolerate a non-JSON body on the error path: a proxy answering 502 with
  // HTML would otherwise raise a bare SyntaxError, losing the status and showing
  // the user a JSON-parser message. The success path keeps throwing as before.
  let body: unknown = null;
  try {
    body = await res.json();
  } catch (e) {
    if (res.ok) throw e;
  }

  if (!res.ok) {
    const isAuthAction = ['/v1/auth/login', '/v1/auth/verify-otp', '/v1/auth/refresh', '/v1/auth/logout'].some(
      (p) => fullPath.startsWith(p),
    );
    if (res.status === 401 && !_isRetry && !isAuthAction) {
      const refreshed = await tryRefresh();
      if (refreshed) return requestRaw<T>(path, options, true);
      // Refresh failed → session is dead, trigger auto-logout
      sessionExpiredCallback?.();
    }
    const retryAfter = (body as { retry_after?: unknown } | null)?.retry_after;
    const field = (body as { field?: unknown } | null)?.field;
    throw new ApiError(
      httpErrorMessage(body, res.status, `Request failed (${res.status})`),
      res.status,
      typeof retryAfter === 'number' ? retryAfter : null,
      typeof field === 'string' ? field : null,
    );
  }

  return body as ApiResponse<T>;
}

/** How far an upload has gone, in bytes. */
export interface UploadProgress {
  loaded: number;
  total: number;
}

/**
 * A multipart POST that reports how much of the body has been sent; `fetch`
 * cannot, so this one goes through XMLHttpRequest. Same contract as `upload`:
 * cookies, CSRF header, one refresh-and-retry on a 401, the `{ success, data }`
 * envelope unwrapped, `ApiError` on failure. Aborting `signal` rejects with an
 * `AbortError`.
 */
function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  { onProgress, signal }: { onProgress?: (p: UploadProgress) => void; signal?: AbortSignal } = {},
  _isRetry = false,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/v1${path}`);
    xhr.withCredentials = true;
    const csrfToken = getCsrfToken();
    if (csrfToken) xhr.setRequestHeader('x-csrf-token', csrfToken);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.({ loaded: e.loaded, total: e.total });
    };
    const abort = () => xhr.abort();
    signal?.addEventListener('abort', abort, { once: true });
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    xhr.onerror = () => reject(new TypeError('Failed to fetch'));
    xhr.onload = () => {
      signal?.removeEventListener('abort', abort);
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        // A non-JSON body only matters on the error path, as in `requestRaw`.
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        const envelope = body as ApiResponse<T> | null;
        resolve((envelope?.data ?? envelope) as T);
        return;
      }
      if (xhr.status === 401 && !_isRetry) {
        tryRefresh().then((refreshed) => {
          if (refreshed) {
            uploadWithProgress<T>(path, formData, { onProgress, signal }, true).then(resolve, reject);
            return;
          }
          sessionExpiredCallback?.();
          reject(new ApiError(httpErrorMessage(body, 401, 'Request failed (401)'), 401));
        }, reject);
        return;
      }
      reject(
        new ApiError(
          httpErrorMessage(body, xhr.status, `Request failed (${xhr.status})`),
          xhr.status,
        ),
      );
    };
    xhr.send(formData);
  });
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const body = await requestRaw<T>(path, options);
  // Backend wraps responses in { success, data }: unwrap
  return body.data ?? (body as unknown as T);
}

// Variant for endpoints that return plain text (e.g. CSV export) instead of JSON.
// Handles auth, CSRF, and token refresh identically to requestRaw.
async function requestText(
  path: string,
  options: RequestInit = {},
  _isRetry = false,
): Promise<string> {
  const fullPath = path.startsWith('/api/') ? path : `/v1${path}`;
  const url = `${API_BASE_URL}${fullPath}`;

  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(options.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  };

  const method = (options.method ?? 'GET').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['x-csrf-token'] = csrfToken;
  }

  const res = await fetch(url, { ...options, credentials: 'include', headers });

  if (!res.ok) {
    const isAuthAction = ['/v1/auth/login', '/v1/auth/verify-otp', '/v1/auth/refresh', '/v1/auth/logout'].some(
      (p) => fullPath.startsWith(p),
    );
    if (res.status === 401 && !_isRetry && !isAuthAction) {
      const refreshed = await tryRefresh();
      if (refreshed) return requestText(path, options, true);
      sessionExpiredCallback?.();
    }
    const fallback = `Request failed (${res.status})`;
    let errorMessage = fallback;
    try {
      errorMessage = httpErrorMessage(await res.json(), res.status, fallback);
    } catch { /* response is not JSON */ }
    throw new ApiError(errorMessage, res.status);
  }

  return res.text();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  getRaw: <T>(path: string, signal?: AbortSignal) => requestRaw<T>(path, { signal }),

  post: <T>(path: string, data?: unknown, headers?: Record<string, string>) =>
    request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...(headers ? { headers } : {}),
    }),

  postRaw: <T>(path: string, data?: unknown) =>
    requestRaw<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(data ?? {}),
    }),

  /** Replace a resource or a set whole (e.g. a licence's main contacts), idempotent. */
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: JSON.stringify(data ?? {}),
    }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type; browser auto-sets multipart boundary
    }),

  /** `upload`, reporting the bytes sent as they go (and cancellable). */
  uploadWithProgress,

  postText: (path: string, data?: unknown) =>
    requestText(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  /** POST that returns a Blob (for binary downloads like xlsx). */
  postBlob: async (path: string, data?: unknown): Promise<Blob> => {
    const fullPath = path.startsWith('/api/') ? path : `/v1${path}`;
    const url = `${API_BASE_URL}${fullPath}`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['x-csrf-token'] = csrfToken;

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!res.ok) {
      if (res.status === 401) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          // Retry once
          const retry = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: data ? JSON.stringify(data) : undefined,
          });
          if (retry.ok) return retry.blob();
        }
        sessionExpiredCallback?.();
      }
      const fallback = `Request failed (${res.status})`;
      let errorMessage = fallback;
      try {
        errorMessage = httpErrorMessage(await res.json(), res.status, fallback);
      } catch { /* response is not JSON */ }
      throw new ApiError(errorMessage, res.status);
    }
    return res.blob();
  },
};

export { ApiError };
export default api;
